<template>
  <div class="gm-stack">
    <nav class="gm-tabs" aria-label="Email design views">
      <button
        v-for="tab in modes"
        :key="tab"
        :class="{ active: mode === tab }"
        @click="mode = tab"
      >
        {{ tab }}
      </button>
    </nav>
    <p v-if="compileError" class="gm-error" role="alert">{{ compileError }}</p>
    <p v-if="/data-groove-needs-link/i.test(html)" class="gm-notice" role="status">This design included a website-only form or link. In Design, replace the marked placeholder link with your website or hosted form before using the email.</p>
    <html-design-editor v-if="mode === 'Design' && !document" :value="customHtml" @input="setHtml" />
    <template v-if="mode === 'Blocks' && document"
      ><div class="gm-toolbar">
        <select v-model="newType" aria-label="New email block type"
          ><option v-for="type in types" :key="type">{{ type }}</option></select
        ><gs-button variant="outline" @click="add">Add block</gs-button
        ><gs-button variant="ghost" @click="mode = 'Preview'">Preview email</gs-button>
      </div>
      <div class="gm-split gm-document-editor">
        <div class="gm-stack">
          <div
            class="gm-email-canvas"
            :style="{ backgroundColor: document.backgroundColor }"
            aria-label="Email content canvas"
          >
            <article
              v-for="(canvasBlock, canvasIndex) in document.blocks"
              :key="canvasBlock.id"
              class="gm-email-block"
              :class="{ 'is-selected': selected === canvasIndex }"
              draggable="true"
              @click="selected = canvasIndex"
              @dragstart="dragIndex = canvasIndex"
              @dragover.prevent
              @drop.prevent="drop(canvasIndex)"
            >
              <div v-if="canvasBlock.type === 'hero'" class="gm-email-block__hero" :style="{ backgroundColor: canvasBlock.backgroundColor }">
                <p v-if="canvasBlock.eyebrow" contenteditable="true" spellcheck="false" @blur="updateEditable(canvasBlock, 'eyebrow', $event)">{{ canvasBlock.eyebrow }}</p>
                <h1 contenteditable="true" spellcheck="false" @blur="updateEditable(canvasBlock, 'title', $event)">{{ canvasBlock.title }}</h1>
                <p contenteditable="true" spellcheck="false" @blur="updateEditable(canvasBlock, 'subtitle', $event)">{{ canvasBlock.subtitle }}</p>
              </div>
              <component
                :is="canvasBlock.level === 1 ? 'h2' : 'h3'"
                v-else-if="canvasBlock.type === 'heading'"
                contenteditable="true"
                spellcheck="false"
                @blur="updateEditable(canvasBlock, 'text', $event)"
              >{{ canvasBlock.text }}</component>
              <div v-else-if="canvasBlock.type === 'text'" class="gm-email-block__text" contenteditable="true" spellcheck="false" @blur="updateEditable(canvasBlock, 'text', $event)">{{ canvasBlock.text }}</div>
              <ul v-else-if="canvasBlock.type === 'list'" class="gm-email-block__list">
                <li v-for="(item, itemIndex) in canvasBlock.items" :key="itemIndex" contenteditable="true" spellcheck="false" @blur="updateListItem(canvasBlock, itemIndex, $event)">{{ item }}</li>
              </ul>
              <div v-else-if="canvasBlock.type === 'callout'" class="gm-email-block__callout" :style="{ backgroundColor: canvasBlock.backgroundColor, borderColor: canvasBlock.borderColor }">
                <strong contenteditable="true" spellcheck="false" @blur="updateEditable(canvasBlock, 'title', $event)">{{ canvasBlock.title }}</strong>
                <div contenteditable="true" spellcheck="false" @blur="updateEditable(canvasBlock, 'text', $event)">{{ canvasBlock.text }}</div>
              </div>
              <blockquote v-else-if="canvasBlock.type === 'quote'">
                <div contenteditable="true" spellcheck="false" @blur="updateEditable(canvasBlock, 'text', $event)">{{ canvasBlock.text }}</div>
                <cite contenteditable="true" spellcheck="false" @blur="updateEditable(canvasBlock, 'attribution', $event)">{{ canvasBlock.attribution }}</cite>
              </blockquote>
              <div v-else-if="canvasBlock.type === 'button'" class="gm-email-block__button" :style="{ backgroundColor: document.accentColor }">
                <span contenteditable="true" spellcheck="false" @blur="updateEditable(canvasBlock, 'label', $event)">{{ canvasBlock.label }}</span>
              </div>
              <div v-else-if="canvasBlock.type === 'image'" class="gm-email-block__image">
                <img v-if="canvasBlock.src" :src="canvasBlock.src" :alt="canvasBlock.alt || 'Email image'" />
                <span v-else>Image block — choose an image in the editor</span>
              </div>
              <div v-else-if="canvasBlock.type === 'divider'" class="gm-email-block__divider" />
              <div v-else-if="canvasBlock.type === 'spacer'" class="gm-email-block__spacer" :style="{ height: Math.max(12, Math.min(120, canvasBlock.height)) + 'px' }" />
              <div v-else class="gm-email-block__fallback">
                <strong>{{ canvasBlock.type }}</strong>
                <span>Use the controls on the right to edit this block.</span>
              </div>
            </article>
          </div>
          <div class="gm-panel">
            <h3>Block order</h3>
            <div
              v-for="(block, index) in document.blocks"
              :key="block.id"
              class="gm-actions"
              draggable="true"
              @dragstart="dragIndex = index"
              @dragover.prevent
              @drop.prevent="drop(index)"
            >
              <gs-button
                variant="ghost"
                :aria-pressed="selected === index"
                @click="selected = index"
                >{{ index + 1 }}. {{ block.type
                }}{{
                  block.title ? ' — ' + block.title.slice(0, 35) : ''
                }}</gs-button
              ><gs-button
                variant="ghost"
                size="icon"
                :disabled="index === 0"
                :aria-label="'Move block ' + (index + 1) + ' up'"
                @click="move(index, -1)"
                ><gs-icon name="chevron-up"/></gs-button
              ><gs-button
                variant="ghost"
                size="icon"
                :disabled="index === document.blocks.length - 1"
                :aria-label="'Move block ' + (index + 1) + ' down'"
                @click="move(index, 1)"
                ><gs-icon name="chevron-down"
              /></gs-button>
            </div>
          </div>
        </div>
        <aside class="gm-panel gm-stack">
          <h2>{{ block ? 'Edit ' + block.type : 'Email style' }}</h2>
          <div class="gm-two">
            <label class="gm-field"
              >Accent<input v-model="document.accentColor" type="color"/></label
            ><label class="gm-field"
              >Background<input v-model="document.backgroundColor" type="color"
            /></label>
          </div>
          <template v-if="block"
            ><label v-for="key in editableKeys" :key="key" class="gm-field"
              >{{ label(key)
              }}<select v-if="choices[key]" v-model="block[key]"
                ><option
                  v-for="option in choices[key]"
                  :key="option"
                  :value="option"
                  >{{ option }}</option
                ></select
              ><textarea
                v-else-if="key === 'items'"
                :value="block.items.join('\n')"
                rows="5"
                @input="
                  block.items = $event.target.value.split('\n')
                "/><textarea
                v-else-if="['text', 'subtitle'].includes(key)"
                v-model="block[key]"
                rows="5"/><input
                v-else-if="typeof block[key] === 'number'"
                v-model.number="block[key]"
                type="number"
                min="0"
                max="500"/><input
                v-else
                v-model="block[key]"
                :type="key.toLowerCase().includes('color') ? 'color' : 'text'"
            /></label>
            <p class="gm-muted">
              Text supports **bold** and [linked text](https://example.com).
              Merge variables stay editable.
            </p>
            <div class="gm-actions">
              <gs-button variant="outline" @click="duplicate"
                >Duplicate block</gs-button
              ><gs-button variant="danger" @click="remove"
                >Remove block</gs-button
              >
            </div></template
          >
        </aside>
      </div></template
    >
    <template v-if="mode === 'HTML'"
      ><div v-if="document" class="gm-notice">
        Editing the HTML makes this an HTML template. Your original professional
        template is unchanged.
        <gs-button
          variant="outline"
          @click="
            customHtml = html;
            document = null;
          "
          >Edit HTML source</gs-button
        >
      </div>
      <html-source :value="html" @input="setHtml"
    /></template>
    <label v-if="mode === 'Text'" class="gm-field"
      >Plain-text version<textarea :value="text" readonly rows="14"
    /></label>
    <template v-if="mode === 'Preview'"
      ><div class="gm-toolbar">
        <gs-button variant="outline" @click="mobile = !mobile">{{
          mobile ? 'Desktop preview' : 'Mobile preview'
        }}</gs-button>
      </div>
      <iframe
        class="gm-preview-frame"
        :class="{ 'gm-preview-mobile': mobile }"
        :srcdoc="preview(html)"
        title="Email preview"
        sandbox=""
    /></template>
  </div>
</template>
<script>
import HtmlSource from './HtmlSource.vue';
import HtmlDesignEditor from './HtmlDesignEditor.vue';
import { compileEmailBlocks, emailBlocksToText } from './data/email-blocks';
import { clone, previewDocument } from './contracts';
const defaults = {
  hero: {
    eyebrow: '',
    title: 'Your headline',
    subtitle: 'A short introduction.',
    backgroundColor: '#eef2ff'
  },
  heading: { text: 'Your heading', level: 1, align: 'left' },
  text: { text: 'Write your message here.', align: 'left' },
  list: { style: 'bullet', items: ['First item', 'Second item'] },
  callout: {
    title: 'Good to know',
    text: 'Add a helpful detail.',
    backgroundColor: '#f8fafc',
    borderColor: '#e2e8f0',
    borderStyle: 'solid',
    textSize: 'normal',
    align: 'left'
  },
  quote: { text: 'A quote worth sharing.', attribution: 'Name' },
  image: { src: '', alt: '', href: '' },
  video: { url: '', thumbnailUrl: '', label: 'Watch the video' },
  button: { label: 'Learn more', url: 'https://example.com', align: 'center' },
  social: {
    facebook: '',
    instagram: '',
    x: '',
    linkedin: '',
    youtube: '',
    tiktok: ''
  },
  divider: {},
  spacer: { height: 24 }
};
export default {
  components: { HtmlSource, HtmlDesignEditor },
  props: { template: { type: Object, required: true } },
  data() {
    return {
      document: this.template.document ? clone(this.template.document) : null,
      customHtml: this.template.bodyHtml || '',
      mode: this.template.document ? 'Blocks' : 'Design',
      selected: 0,
      newType: 'text',
      types: Object.keys(defaults),
      mobile: false,
      dragIndex: null,
      choices: {
        align: ['left', 'center', 'right'],
        level: [1, 2],
        style: ['number', 'bullet', 'check'],
        borderStyle: ['solid', 'dashed', 'none'],
        textSize: ['normal', 'large']
      }
    };
  },
  computed: {
    modes() {
      return this.document
        ? ['Blocks', 'HTML', 'Text', 'Preview']
        : ['Design', 'HTML', 'Text', 'Preview'];
    },
    rendered() {
      try {
        return {
          html: this.document
            ? compileEmailBlocks(this.document)
            : this.customHtml,
          error: ''
        };
      } catch (error) {
        return {
          html: '',
          error: 'Check the block values before saving: ' + error.message
        };
      }
    },
    html() {
      return this.rendered.html;
    },
    compileError() {
      return this.rendered.error;
    },
    text() {
      return this.document
        ? emailBlocksToText(this.document)
        : new DOMParser().parseFromString(this.customHtml, 'text/html').body
            .textContent || '';
    },
    block() {
      return this.document && this.document.blocks[this.selected];
    },
    editableKeys() {
      return this.block
        ? Object.keys(this.block).filter(key => !['id', 'type'].includes(key))
        : [];
    },
    result() {
      return {
        document: this.document,
        bodyHtml: this.html,
        bodyText: this.text
      };
    }
  },
  watch: {
    result: {
      deep: true,
      handler(value) {
        this.$emit('change', clone(value));
      }
    }
  },
  methods: {
    preview: previewDocument,
    label(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, character => character.toUpperCase());
    },
    updateEditable(block, key, event) {
      this.$set(block, key, event.target.innerText);
    },
    updateListItem(block, index, event) {
      this.$set(block.items, index, event.target.innerText);
    },
    add() {
      if (this.document.blocks.length >= 100) return;
      this.document.blocks.push(
        Object.assign(
          {
            id:
              'block-' +
              Date.now() +
              '-' +
              Math.random()
                .toString(36)
                .slice(2),
            type: this.newType
          },
          clone(defaults[this.newType])
        )
      );
      this.selected = this.document.blocks.length - 1;
    },
    duplicate() {
      this.document.blocks.splice(
        this.selected + 1,
        0,
        Object.assign(clone(this.block), { id: 'copy-' + Date.now() })
      );
      this.selected++;
    },
    remove() {
      this.document.blocks.splice(this.selected, 1);
      this.selected = Math.max(0, this.selected - 1);
    },
    move(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.document.blocks.length) return;
      const item = this.document.blocks.splice(index, 1)[0];
      this.document.blocks.splice(target, 0, item);
      this.selected = target;
    },
    drop(index) {
      if (this.dragIndex === null) return;
      this.move(this.dragIndex, index - this.dragIndex);
      this.dragIndex = null;
    },
    setHtml(html) {
      if (!this.document) this.customHtml = html;
    }
  }
};
</script>
<style scoped>
.gm-document-editor {
  align-items: stretch;
}

.gm-email-canvas {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  min-height: 520px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
}

.gm-email-block {
  position: relative;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: var(--gs-radius-sm);
  background: var(--gs-surface);
  cursor: grab;
}

.gm-email-block:hover,
.gm-email-block.is-selected {
  border-color: var(--gs-active);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--gs-active) 18%, transparent);
}

.gm-email-block:active { cursor: grabbing; }
.gm-email-block [contenteditable='true'] { outline: 0; cursor: text; }
.gm-email-block [contenteditable='true']:focus { box-shadow: inset 0 -2px 0 var(--gs-active); }
.gm-email-block__hero { padding: 34px 28px; color: #fff; text-align: center; border-radius: 6px; }
.gm-email-block__hero h1 { margin: 6px 0; font-size: 28px; line-height: 1.2; }
.gm-email-block__hero p { margin: 4px 0; }
.gm-email-block__text { white-space: pre-wrap; padding: 12px 8px; line-height: 1.6; }
.gm-email-block__list { margin: 8px 0; padding-left: 28px; line-height: 1.7; }
.gm-email-block__callout { padding: 18px 20px; border: 2px solid; border-radius: 8px; line-height: 1.6; }
.gm-email-block__callout strong { display: block; margin-bottom: 6px; }
.gm-email-block blockquote { margin: 8px 0; padding: 6px 0 6px 18px; border-left: 3px solid var(--gs-active); line-height: 1.6; }
.gm-email-block blockquote cite { display: block; margin-top: 8px; color: var(--gs-muted-foreground); font-style: normal; }
.gm-email-block__button { width: max-content; max-width: 100%; margin: 8px auto; padding: 12px 28px; color: #fff; border-radius: 6px; font-weight: 700; }
.gm-email-block__image { min-height: 90px; display: grid; place-items: center; color: var(--gs-muted-foreground); background: var(--gs-surface-subtle); }
.gm-email-block__image img { display: block; max-width: 100%; max-height: 220px; margin: auto; }
.gm-email-block__divider { border-top: 1px solid var(--gs-border-strong); margin: 12px 8px; }
.gm-email-block__spacer { min-height: 12px; }
.gm-email-block__fallback { display: flex; justify-content: space-between; gap: 12px; padding: 16px; color: var(--gs-muted-foreground); }

@media (max-width: 900px) {
  .gm-email-canvas { max-height: none; }
  .gm-email-block { max-width: none; }
}
</style>
